import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import CircularProgress from "@material-ui/core/CircularProgress";
import FinanceService from "Lib/API/Finance";

const RecentTransactionsWidgetConfig = ({ open, isNew, onClose, onSave, initialSettings }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [label, setLabel] = useState("");
  const [ledgers, setLedgers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedMeta, setSelectedMeta] = useState([]);

  useEffect(() => {
    if (!open) return;
    setLabel(initialSettings?.label || "");
    setSelectedMeta(initialSettings?.ledgerMeta || []);
    setLoading(true);
    getAccessTokenSilently()
      .then((token) => FinanceService.getLedgerSubLedgerList(token))
      .then((res) => { if (res?.ledgers) setLedgers(res.ledgers); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open, initialSettings, getAccessTokenSilently]);

  const isSelected = (id) => selectedMeta.some((m) => m.id === id);

  const toggle = (item) => {
    setSelectedMeta((prev) =>
      prev.some((m) => m.id === item.id)
        ? prev.filter((m) => m.id !== item.id)
        : [...prev, item]
    );
  };

  const handleSave = () => {
    const ledgerIds = selectedMeta.filter((m) => m.type === "ledger").map((m) => m.id);
    const subLedgerIds = selectedMeta.filter((m) => m.type === "sub_ledger").map((m) => m.id);
    onSave({ label: label.trim(), ledger: ledgerIds, subLedger: subLedgerIds, ledgerMeta: selectedMeta });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle style={{ paddingBottom: 8 }}>{isNew ? "Add Recent Transactions Widget" : "Configure Recent Transactions Widget"}</DialogTitle>
      <DialogContent style={{ paddingTop: 4 }}>
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>
            Widget Label <span style={{ fontWeight: 400, color: "#9ca3af" }}>(optional)</span>
          </label>
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Building Fund Transactions"
            style={{
              width: "100%", boxSizing: "border-box",
              padding: "7px 10px", border: "1px solid #d1d5db",
              borderRadius: 6, fontSize: 13, outline: "none",
            }}
          />
        </div>

        <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>
          Filter by Ledger / Sub-Ledger
          <span style={{ fontWeight: 400, color: "#9ca3af" }}> — leave empty to show all</span>
        </div>
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 16 }}>
            <CircularProgress size={24} />
          </div>
        ) : ledgers.length === 0 ? (
          <div style={{ fontSize: 13, color: "#9ca3af" }}>No ledgers available</div>
        ) : (
          <div style={{ maxHeight: 280, overflowY: "auto", border: "1px solid #e5e7eb", borderRadius: 6 }}>
            {ledgers.map((ledger) => (
              <div key={ledger._id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                <label style={{ display: "flex", alignItems: "center", padding: "6px 10px", cursor: "pointer", gap: 4 }}>
                  <Checkbox
                    size="small"
                    color="primary"
                    checked={isSelected(ledger._id)}
                    onChange={() => toggle({ label: ledger.ledger_name, id: ledger._id, type: "ledger" })}
                    style={{ padding: 2 }}
                  />
                  <span style={{ fontSize: 13, fontWeight: 500, color: "#111827" }}>{ledger.ledger_name}</span>
                </label>
                {ledger.sub_ledgers?.map((sub) => (
                  <label
                    key={sub._id}
                    style={{ display: "flex", alignItems: "center", padding: "4px 10px 4px 32px", cursor: "pointer", gap: 4, background: "#fafafa" }}
                  >
                    <Checkbox
                      size="small"
                      color="primary"
                      checked={isSelected(sub._id)}
                      onChange={() => toggle({ label: sub.sub_ledger_name, id: sub._id, type: "sub_ledger" })}
                      style={{ padding: 2 }}
                    />
                    <span style={{ fontSize: 12.5, color: "#4b5563" }}>{sub.sub_ledger_name}</span>
                  </label>
                ))}
              </div>
            ))}
          </div>
        )}
        <div style={{ marginTop: 8, fontSize: 12, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ color: selectedMeta.length > 0 ? "#2455ef" : "#9ca3af" }}>
            {selectedMeta.length > 0
              ? `${selectedMeta.length} item${selectedMeta.length !== 1 ? "s" : ""} selected`
              : "All ledgers (no filter)"}
          </span>
          {selectedMeta.length > 0 && (
            <button
              onClick={() => setSelectedMeta([])}
              style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, color: "#6b7280", padding: 0, textDecoration: "underline" }}
            >
              Clear
            </button>
          )}
        </div>
      </DialogContent>
      <DialogActions style={{ padding: "12px 16px" }}>
        <Button onClick={onClose} size="small">Cancel</Button>
        <Button onClick={handleSave} color="primary" variant="contained" size="small" disableElevation>
          {isNew ? "Add to Dashboard" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecentTransactionsWidgetConfig;
